const CHANNEL_NAME = 'cop-session'
const CUSTOMER_ID_KEY = 'cop:customer-id'
const CUSTOMER_NAME_KEY = 'cop:customer-name'
const PERMISSIONS_KEY = 'cop:permissions'
const SYNC_TIMEOUT_MS = 400

type SessionMessage =
    | { type: 'REQUEST_SESSION' }
    | { type: 'SESSION_DATA'; customerId: string; customerName: string; permissions?: string }

let channel: BroadcastChannel | null = null
let pendingSyncPromise: Promise<void> | null = null

const openChannel = (): BroadcastChannel | null => {
    if (typeof BroadcastChannel === 'undefined') return null
    if (!channel) {
        channel = new BroadcastChannel(CHANNEL_NAME)
        // Respond to session requests from new tabs
        channel.addEventListener('message', (e: MessageEvent<SessionMessage>) => {
            if (e.data?.type !== 'REQUEST_SESSION') return
            const customerId = sessionStorage.getItem(CUSTOMER_ID_KEY)
            if (customerId) {
                const permissions = sessionStorage.getItem(PERMISSIONS_KEY)
                channel?.postMessage({
                    type: 'SESSION_DATA',
                    customerId,
                    customerName: sessionStorage.getItem(CUSTOMER_NAME_KEY) ?? '',
                    ...(permissions ? { permissions } : {}),
                } satisfies SessionMessage)
            }
        })
    }
    return channel
}

/** Call once per page so this tab can respond to session requests from new tabs. */
export const initSessionSync = (): void => {
    openChannel()
}

/**
 * If sessionStorage has no customer-id, requests it from an existing tab via
 * BroadcastChannel and waits up to SYNC_TIMEOUT_MS for a response.
 * Resolves immediately when the session is already present.
 */
export const waitForSessionSync = (): Promise<void> => {
    const hasCustomerId = Boolean(sessionStorage.getItem(CUSTOMER_ID_KEY))
    const hasCustomerName = Boolean(sessionStorage.getItem(CUSTOMER_NAME_KEY))
    const hasPermissions = Boolean(sessionStorage.getItem(PERMISSIONS_KEY))
    if (hasCustomerId && hasCustomerName && hasPermissions) return Promise.resolve()
    if (pendingSyncPromise) return pendingSyncPromise

    const bc = openChannel()
    if (!bc) return Promise.resolve()

    pendingSyncPromise = new Promise<void>((resolve) => {
        const timer = window.setTimeout(() => {
            bc.removeEventListener('message', onData as EventListener)
            pendingSyncPromise = null
            resolve()
        }, SYNC_TIMEOUT_MS)

        const onData = (e: MessageEvent<SessionMessage>): void => {
            if (e.data?.type !== 'SESSION_DATA' || !e.data.customerId) return
            sessionStorage.setItem(CUSTOMER_ID_KEY, e.data.customerId)
            if (e.data.customerName) sessionStorage.setItem(CUSTOMER_NAME_KEY, e.data.customerName)
            if (e.data.permissions) sessionStorage.setItem(PERMISSIONS_KEY, e.data.permissions)
            clearTimeout(timer)
            bc.removeEventListener('message', onData as EventListener)
            pendingSyncPromise = null
            resolve()
        }

        bc.addEventListener('message', onData as EventListener)
        bc.postMessage({ type: 'REQUEST_SESSION' } satisfies SessionMessage)
    })

    return pendingSyncPromise
}
